import IconCatch from '@/components/icons/icon-catch/icon-catch';
import { headerHeightLg } from '@/constants/layouts';
import { ROUTE_ACCOUNT, ROUTE_DASHBOARD } from '@/constants/routes';
import { Portal } from '@chakra-ui/react';
import { Button, Flex, Image, Link, Popover, Text } from '@cmpsr/components';
import { Link as ReactRouterLink } from 'react-router-dom';
import { DashboardNavigationBarProps } from '../types';
import NavigationBarSelectedItem from './navigation-bar-selected-item';

export default function DashboardNavigationBarDesktop({
  handleLogout,
  menuItems,
  activeItem,
  containerProps,
  navBarRef,
  variant = 'dark',
}: DashboardNavigationBarProps) {
  const isLight = variant === 'light';

  return (
    <Flex
      as="nav"
      className={isLight ? '' : 'backdrop-blur-md'}
      justifyContent="center"
      alignItems="center"
      height={headerHeightLg}
      px={{ lg: 'spacer-16', xl: 'spacer-24', xxl: 'spacer-32' }}
      py="spacer-6"
      w="100%"
      overflow="hidden"
      background={isLight ? 'white' : undefined}
      borderBottom={isLight ? '1px solid' : undefined}
      borderColor={isLight ? 'gray.200' : undefined}
      _before={
        isLight
          ? undefined
          : {
              content: '""',
              position: 'absolute',
              top: '-60px',
              left: '-60px',
              right: '-60px',
              bottom: '-60px',
              filter: 'blur(30px)',
              zIndex: -1,
              clipPath: 'inset(0 0 60px 0)',
            }
      }
      {...containerProps}
      ref={navBarRef}
    >
      <Flex width="100%" maxW="1184px" justifyContent="space-between" alignItems="center" gap="10px">
        <Link
          width="auto"
          as={ReactRouterLink}
          to={ROUTE_DASHBOARD}
          aria-label="Navigate home"
          alignItems="center"
          justifyContent="flex-start"
          gap={0}
        >
          <Image
            src={isLight ? '/images/dark-catch-logo-spelled.svg' : '/images/logo-catch.svg'}
            width="140px"
            height="25px"
            alt="Catch logo"
          />
          <Text
            as="span"
            display="inline-block"
            mt="-25px"
            color={isLight ? '#183344' : 'white'}
            fontSize="12px"
            fontWeight="400"
          >
            beta
          </Text>
        </Link>
        <Flex as="ul" listStyleType="none" alignItems="center" justifyContent="center">
          {menuItems.map((menuItem) =>
            menuItem.item === activeItem ? (
              <NavigationBarSelectedItem key={menuItem.item} label={menuItem.label} variant={variant} />
            ) : (
              <Link
                key={menuItem.item}
                to={menuItem.href}
                as={ReactRouterLink}
                variant={isLight ? 'link-primary' : 'link-light'}
                px={4}
                py={7}
              >
                {menuItem.label}
              </Link>
            ),
          )}
        </Flex>
        <Popover placement="bottom-end">
          <Popover.Trigger>
            <Button aria-label="open account options" variant="link-ghost" p={0} borderRadius="50%" overflow="hidden">
              <IconCatch
                icon="Profile-User"
                height="40px"
                width="40px"
                backgroundColor="lightBlue"
                p={2}
                alt="User icon"
              />
            </Button>
          </Popover.Trigger>
          <Portal>
            <Popover.Content w="205px">
              <Popover.Body display="flex" flexDirection="column" alignContent="start">
                <Link variant="ghost" as={ReactRouterLink} to={ROUTE_ACCOUNT} w="100%">
                  Account
                </Link>
              </Popover.Body>
              <Popover.Footer>
                <Link role="button" onClick={handleLogout} variant="ghost" w="100%">
                  Log out
                </Link>
              </Popover.Footer>
            </Popover.Content>
          </Portal>
        </Popover>
      </Flex>
    </Flex>
  );
}
